import type { QueryClient } from "react-query";

type SettledQueryInternals = {
  /** Query.fetch stores the Retryer's promise here for in-flight deduplication. */
  promise?: Promise<unknown>;
  /** React Query v3 leaves the settled Retryer attached to its Query. */
  retryer?: { isResolved: boolean };
};

/**
 * Releases React Query v3 fetch bookkeeping that otherwise duplicates cached
 * assistant-session payloads.
 *
 * Query.setData structurally shares deep-equal data with the previous cache
 * value. Query.fetch still keeps the newly parsed response reachable through
 * both Query.promise and Query.retryer.promise after the fetch settles. Large,
 * unchanged sessions therefore retain the shared cache tree plus a byte-equal
 * response tree until the query is evicted.
 *
 * Structural sharing must stay enabled because the thread store and message
 * transform caches use stable message identity to skip no-op refetch work.
 */
export function subscribeToAssistantSessionQueryPromiseCleanup(
  queryClient: QueryClient,
): () => void {
  return queryClient.getQueryCache().subscribe((event) => {
    if (
      event?.type !== "queryUpdated" ||
      (event.action.type !== "success" && event.action.type !== "error") ||
      event.query.queryKey[0] !== "assistantSession"
    ) {
      return;
    }

    // These are private React Query v3 fields. Query.fetch only reads promise
    // while state.isFetching is true, so releasing both references after the
    // Retryer resolves preserves in-flight request deduplication.
    const internals = event.query as unknown as SettledQueryInternals;

    // Observer callbacks run before cache subscribers and may synchronously
    // start another fetch. Also, setQueryData dispatches a success action and
    // clears isFetching while the current Retryer is still active. Both guards
    // keep that live fetch's bookkeeping intact.
    if (event.query.state.isFetching || !internals.retryer?.isResolved) return;

    // Retryer.promise is the same settled promise as Query.promise. Clearing
    // only Query.promise would leave the duplicate response tree reachable.
    internals.promise = undefined;
    internals.retryer = undefined;
  });
}
